import { cn } from '@/lib/utils';

/**
 * The square that identifies a workspace at a glance.
 *
 * ⭐ COLOUR IS DERIVED FROM THE UID, NOT STORED AND NOT RANDOM. A merchant
 * recognises their store by its colour long before they read the name, so the
 * same workspace must be the same colour on every render, every device and
 * every session — which a stored field would eventually contradict and
 * `Math.random()` would break immediately. Hashing the uid gives that for free
 * and needs no migration.
 *
 * Eight hues, fixed and curated: far enough apart to tell at 24px, all at ~600
 * weight so white text clears AA on every one of them.
 *
 * ⚠️ THE BRAND YELLOW IS NOT IN IT, and must not be. Yellow is reserved for
 * primary actions and focus rings — a workspace that happened to hash to it
 * would look like a button, and at ≈1.3:1 it cannot carry white initials.
 */
const PALETTE = [
  'bg-[#4f46e5]', // indigo
  'bg-[#0d9488]', // teal
  'bg-[#e11d48]', // rose
  'bg-[#d97706]', // amber
  'bg-[#7c3aed]', // violet
  'bg-[#059669]', // emerald
  'bg-[#0284c7]', // sky
  'bg-[#475569]', // slate
];

export const workspaceTint = (uid: string): string => {
  let hash = 0;
  for (let i = 0; i < uid.length; i += 1) {
    hash = (hash * 31 + uid.charCodeAt(i)) | 0;
  }
  return PALETTE[Math.abs(hash) % PALETTE.length];
};

export const workspaceInitials = (name?: string | null): string => {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return '#';
  if (words.length === 1) return words[0].slice(0, 1).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

interface Props {
  uid: string;
  name?: string | null;
  /** Menu rows | card/menu default | page cards. */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

const SIZES = {
  sm: 'h-6 w-6 rounded-md text-[10px]',
  md: 'h-8 w-8 rounded-lg text-[12px]',
  lg: 'h-10 w-10 rounded-[10px] text-sm',
} as const;

const WorkspaceTile = ({ uid, name, size = 'md', className }: Props) => (
  <span
    aria-hidden
    className={cn(
      'flex shrink-0 items-center justify-center font-semibold text-white',
      SIZES[size],
      workspaceTint(uid),
      className,
    )}
  >
    {workspaceInitials(name)}
  </span>
);

export default WorkspaceTile;
